import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { format, parseISO, addYears, addDays } from 'date-fns';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { UpdateModal } from '@app/app/esales/transport/components/UpdateModal';

interface Props {
    beginDate: string;
    onEdit: () => void;
}

export function InsuranceStartDatePanel({ beginDate, onEdit }: Props) {
    const t = useTranslations();
    const [updateModalOpen, setUpdateModalOpen] = useState(false);

    const calculateEndDate = (startDate: string): string => {
        if (!startDate) return '';

        try {
            const date = parseISO(startDate);
            if (isNaN(date.getTime())) return '';

            const endDate = addDays(addYears(date, 1), -1);
            return format(endDate, 'yyyy-MM-dd');
        } catch {
            return '';
        }
    };

    const formatDisplayDate = (dateString: string): string => {
        if (!dateString) return '';
        try {
            return format(parseISO(dateString), 'yyyy-MM-dd');
        } catch {
            return dateString;
        }
    };

    const handleOpenUpdateModal = () => {
        setUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setUpdateModalOpen(false);
    };

    const handleProceed = () => {
        handleCloseUpdateModal();
        onEdit();
    };

    return (
        <>
            <CollapsablePanel
                label={t('eshop.personalAccidents.coveragePeriod')}
                collapsable={false}
                addon={<TextButton onClick={handleOpenUpdateModal}>{t('general.edit')}</TextButton>}>
                <div className="px-6 py-2">
                    <div className="grid grid-cols-2 gap-4 max-xs:grid-cols-1">
                        <ReadOnlyField label={t('eshop.personalAccidents.insuranceStartDateLabel')}>
                            {formatDisplayDate(beginDate)}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.personalAccidents.endDateLabel')}>
                            {calculateEndDate(beginDate)}
                        </ReadOnlyField>
                    </div>
                </div>
            </CollapsablePanel>

            <UpdateModal
                updateModalOpen={updateModalOpen}
                handleCloseUpdateModal={handleCloseUpdateModal}
                navigateToStep={handleProceed}
            />
        </>
    );
}
